import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminFormPage } from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { ProjectAttachmentForm } from "../ProjectAttachmentForm/ProjectAttachmentForm";
import { useProjectAttachmentMutations } from "../api/useProjectAttachmentMutations";
import { useAttachmentTypeOptions } from "../api/useAttachmentTypeOptions";
import { useProjectAttachmentsList } from "../api/useProjectAttachmentsList";
import {
  emptyProjectAttachmentForm,
  type ProjectAttachmentFormValues,
} from "../types";

const FORM_ID = "project-attachment-form";

export interface NewProps {
  projectId: string;
}

const New = ({ projectId }: NewProps): JSX.Element => {
  const navigate = useNavigate();
  const { create, uploadFile } = useProjectAttachmentMutations();
  const { options } = useAttachmentTypeOptions();
  // Hide attachment types already used on this project (uniqueness per project).
  const { rows } = useProjectAttachmentsList({ projectId, active: true });
  const usedTypeIds = useMemo(
    () =>
      new Set(rows.map((r) => r.attachmentTypeId).filter(Boolean) as string[]),
    [rows]
  );
  const typeOptions = useMemo(
    () => options.filter((o) => !usedTypeIds.has(o.value)),
    [options, usedTypeIds]
  );

  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{
    variant: "success" | "danger";
    text: string;
  } | null>(null);

  const handleSubmit = async (values: ProjectAttachmentFormValues) => {
    if (!values.file) return; // guarded in the form, defensive here
    setSubmitting(true);
    try {
      const result: any = await create(
        { attachmentTypeId: values.attachmentTypeId },
        { projectId }
      );
      const newId = result?.data?.id;
      await uploadFile(newId, values.file);
      navigate("..", {
        relative: "path",
        state: { toast: { variant: "success", text: "Attachment saved" } },
      });
    } catch (err: any) {
      const detail = err?.data?.errors?.[0]?.detail;
      const isDup =
        typeof detail === "string" && /attachment type/i.test(detail);
      setToast({
        variant: "danger",
        text: isDup
          ? "An attachment of this type already exists for this project."
          : "Could not save the attachment.",
      });
      setSubmitting(false);
    }
  };

  return (
    <>
      <AdminFormPage
        title="Add an Attachment"
        formId={FORM_ID}
        onCancel={() => navigate("..", { relative: "path" })}
        isSubmitting={submitting}
      >
        <ProjectAttachmentForm
          id={FORM_ID}
          mode="new"
          initialValues={emptyProjectAttachmentForm}
          typeOptions={typeOptions}
          currentFilename={null}
          onSubmit={handleSubmit}
        />
      </AdminFormPage>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default New;
